import React, { useState, useEffect } from "react";
import { Typography } from "antd";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import ActionsTable from "../../components/templates/ActionsTable";
import AvatarComponent from "../../components/tables/AvatarComponent";
import Currency from "../../components/tables/Currency";
import { ReactComponent as BranchesImg } from "../../assets/no-branches.svg";
import jsPDF from "jspdf";
import Header from "./Header";
import {
  loadPage,
  createPaymentRequests,
  createDryRunPaymentRequests,
  reset,
  reload,
} from "./paymentRequestsSlice";
import {
  getItems,
  getColumns,
  setHeader,
  setBottomContent,
} from "../../components/print/printSections/prospectus";
import { getProspectusDoc } from "../../components/util/helpers";
import { getFullName } from "../../components/util/helpers";
import { useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
const { Text } = Typography;

const PaymentRequests = () => {
  const intl = useIntl();
  const [printDemo, setPrintDemo] = useState(false);
  const [printReal, setPrintReal] = useState(false);

  let location = useLocation();
  const { page: paymentRequestsPage } = useSelector(
    (state) => state.paymentRequests
  );
  const activeBranch = useSelector(
    (state) => state.paymentRequests.activeBranch
  );
  const activeClient = useSelector(
    (state) => state.paymentRequests.activeClient
  );
  const activeCategory = useSelector(
    (state) => state.paymentRequests.activeCategory
  );
  const activeYear = useSelector((state) => state.paymentRequests.activeYear);
  const activeMonth = useSelector((state) => state.paymentRequests.activeMonth);
  const clients = useSelector((state) => state.paymentRequests.clients);

  const organization = useSelector((state) => state.organization.details);
  const generatedProspectuses = useSelector(
    (state) => state.paymentRequests.generatedProspectuses
  );
  const dryRunProspectuses = useSelector(
    (state) => state.paymentRequests.dryRunProspectuses
  );
  const { current, pageSize, total, items, filter } = paymentRequestsPage;
  const loading = useSelector(
    (state) => state.pendingActions["paymentRequests/loadPage"]
  );
  const createLoading = useSelector(
    (state) => state.pendingActions["paymentRequests/create"]
  );
  const isAdmin = location.pathname.includes("admin");
  const dispatch = useDispatch();

  useEffect(() => {
    if (activeCategory && (!isAdmin || activeBranch)) {
      dispatch(loadPage());
    }
  }, [
    activeYear,
    activeMonth,
    activeClient,
    activeCategory,
    dispatch,
    isAdmin,
    activeBranch,
    clients,
  ]);

  useEffect(() => {
    return function cleanup() {
      dispatch(reset());
    };
  }, [dispatch]);

  useEffect(() => {
    if (dryRunProspectuses && dryRunProspectuses.length > 0 && printDemo) {
      let prospectuses = new jsPDF({ orientation: "l" });
      dryRunProspectuses.forEach((item, i) => {
        if (i > 0) {
          prospectuses.addPage();
          const last = prospectuses.getNumberOfPages();
          prospectuses.setPage(last);
        }
        prospectuses = getProspectusDoc(
          item.lines,
          item,
          prospectuses,
          organization,
          getItems,
          getColumns,
          intl,
          setHeader,
          setBottomContent
        );
      });
      window.open(prospectuses.output("bloburl"), "_blank");
      setPrintDemo(false);
    }
  }, [dryRunProspectuses, printDemo]);

  useEffect(() => {
    if (
      generatedProspectuses &&
      generatedProspectuses.length > 0 &&
      printReal
    ) {
      let prospectuses = new jsPDF({ orientation: "l" });
      generatedProspectuses.forEach((item, i) => {
        if (i > 0) {
          prospectuses.addPage();
          const last = prospectuses.getNumberOfPages();
          prospectuses.setPage(last);
        }
        prospectuses = getProspectusDoc(
          item.lines,
          item,
          prospectuses,
          organization,
          getItems,
          getColumns,
          intl,
          setHeader,
          setBottomContent
        );
      });
      window.open(prospectuses.output("bloburl"), "_blank");
      setPrintReal(false);
      dispatch(loadPage());
    }
  }, [generatedProspectuses, printReal]);

  const onCreatePaymentRequests = async () => {
    await dispatch(createPaymentRequests());
    setPrintReal(true);
  };

  const onPrintDemo = async () => {
    await dispatch(createDryRunPaymentRequests());
    setPrintDemo(true);
  };

  const tableItems = [
    {
      title: "avatar_url",
      render: (r) => {
        return (
          <AvatarComponent
            record={r.record["order"].patient}
            text={r.record["order"].patient.avatar_url}
          />
        );
      },
      width: "60px",
    },
    {
      title: "patient_name",
      width: "120px",
      ellipsis: true,
      render: (r) => {
        return (
          <Text>
            {getFullName(
              r.record["order"].patient.surename,
              r.record["order"].patient.first_name
            )}
          </Text>
        );
      },
    },
    {
      title: "patient_local_id_number",
      width: "100px",
      render: (r) => {
        return (
          <Text ellipsis>{r.record["order"].patient.local_id_number}</Text>
        );
      },
    },
    {
      title: "start_of_period",
      width: "100px",
      render: (r) => {
        const date = moment()
          .set({ year: r.record["year"], month: r.record["month"] - 1 })
          .startOf("month");
        const dateFormat = "DD/MM/YYYY";
        return <Text>{date.format(dateFormat)}</Text>;
      },
    },
    {
      title: "end_of_period",
      width: "100px",
      render: (r) => {
        const date = moment()
          .set({ year: r.record["year"], month: r.record["month"] - 1 })
          .endOf("month");
        const dateFormat = "DD/MM/YYYY";
        return <Text>{date.format(dateFormat)}</Text>;
      },
    },
    {
      title: "employee_name",
      width: "120px",
      render: (r) => {
        const employee = r.record["employee"];
        return employee ? (
          <Text>{getFullName(employee.surename, employee.first_name)}</Text>
        ) : r.record?.order?.order_service_type?.supporting_community ? (
          <Text>
            {intl.formatMessage({
              id: "app.scheduling.supporting_community",
            })}
          </Text>
        ) : (
          r.record?.order?.order_service_type?.day_center && (
            <Text>
              {intl.formatMessage({
                id: "app.scheduling.day_center",
              })}
            </Text>
          )
        );
      },
    },
    {
      title: "units_count",
      width: "80px",
      render: (r) => {
        return <Text>{r.record["units_count"]}</Text>;
      },
    },
    {
      title: "tariff",
      width: "80px",
      render: (r) => {
        return <Text>{r.record["tariff"]}</Text>;
      },
    },
    {
      title: "charged_sum",
      width: "100px",
      render: (r) => {
        const total = r.record["units_count"] * r.record["tariff"];
        return <Currency text={total} />;
      },
    },
  ];

  return (
    <div style={{ paddingTop: "0" }}>
      <Header
        onCreatePaymentRequests={onCreatePaymentRequests}
        onPrintDemo={onPrintDemo}
        disabled={!items || items.length < 1}
        loading={createLoading}
        items={items.map((i, index) => ({ ...i, id: index }))}
      />
      <div style={{ height: "10px" }} />
      <ActionsTable
        deep={true}
        tableItems={tableItems}
        category={"paymentRequests"}
        resource="paymentRequests"
        current={current}
        pageSize={pageSize}
        total={total}
        loading={loading}
        textFilter={filter.freeText}
        reload={reload}
        loadPage={loadPage}
        items={items.map((i, index) => ({ ...i, id: index }))}
        showheader={false}
        linkType={"branches"}
        emptyImage={<BranchesImg />}
        skipInitialLoad={true}
      />
    </div>
  );
};

export default PaymentRequests;
